import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select, { components, createFilter } from 'react-select';
import { Box, TextField, Typography, Button, IconButton, useMediaQuery } from '@mui/material';
import { LocalizationProvider, DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import calendarIcon from '../../assets/icons/calendar.svg';
import xIcon from '../../assets/icons/x.svg';
import { CustomDropdownIndicator, MultiselectValueContainer, MultiselectOption } from './MultiselectComponents';
import { useMultiselectMenuControl } from '../../hooks/useMultiselectMenuControl';

const FilterInput = ({
  type = 'text',
  name = '',
  label = '',
  options = [],
  value,
  onChange,
  onClear,
  disabled = false,
  placeholder = '',
  required = false,
  hasValue = false,
  isMultiSelect = false,
  isSearchable,
  highlightOptionValues,
  onDatePickerOpenChange,
  ...props
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const isOpeningRef = useRef(false);
  
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const datePickerRef = useRef(null);
  const datePickerCalendarRef = useRef(null);
  
  // Debounced search state
  const [searchValue, setSearchValue] = useState(value || '');
  const debounceTimeoutRef = useRef(null);
  
  // Use shared hook for multiselect menu control
  const { menuIsOpen, setMenuIsOpen, selectWrapperRef, lastActionRef, shouldIgnoreCloseFromReactSelect } = useMultiselectMenuControl(isMultiSelect);
  const isMobile = useMediaQuery('(max-width:768px)');

  // Date range helper functions
  const isDateInRange = (date, start, end) => {
    if (!start || !end) return false;
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    
    return dateTime > startTime && dateTime < endTime;
  };

  const isDateStartOrEnd = (date, start, end) => {
    if (!start || !end) return false;
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    
    return dateTime === startTime || dateTime === endTime;
  };

  // Custom day component for range highlighting
  const CustomPickersDay = (props) => {
    const { day, ...other } = props;
    const isInRange = isDateInRange(day, startDate, endDate);
    const isStartOrEnd = isDateStartOrEnd(day, startDate, endDate);
    
    // Check if this is specifically the start or end date
    const isStartDate = startDate && day.getTime() === new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    const isEndDate = endDate && day.getTime() === new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
    
    return (
      <PickersDay
        {...other}
        day={day}
        onTouchEnd={(e) => {
          e.preventDefault();
          const btn = e.currentTarget;
          if (btn && typeof btn.click === 'function') btn.click();
        }}
        sx={{
          fontFamily: 'Noto Sans',
          fontWeight: 700,
          color: '#000000',
          borderRadius: '12px',
          touchAction: 'manipulation',
          ...(isMobile && { minWidth: 44, minHeight: 44 }),
          ...(isInRange && {
            backgroundColor: '#E5E5E5',
            color: '#666666',
            '&:hover': {
              backgroundColor: '#D5D5D5',
            },
          }),
          ...(isStartOrEnd && {
            backgroundColor: '#027BC6',
            color: 'white',
            boxShadow: '0px 4px 10px #6A78F819',
            '&:hover': {
              backgroundColor: '#027BC6',
            },
          }),
          // Ensure both start and end dates have the same solid blue color
          ...(isStartDate && {
            backgroundColor: '#027BC6 !important',
            color: 'white !important',
            boxShadow: '0px 4px 10px #6A78F819 !important',
          }),
          ...(isEndDate && {
            backgroundColor: '#027BC6 !important',
            color: 'white !important',
            boxShadow: '0px 4px 10px #6A78F819 !important',
          }),
        }}
      />
    );
  };

  // Custom DropdownIndicator for single select (still needs X icon)
  const SingleSelectDropdownIndicator = (props) => {
    const { hasValue, onClear } = props;
    
    // For single select with value, show X
    if (hasValue && onClear) {
      return (
        <components.DropdownIndicator {...props}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onClear();
            }}
            onTouchEnd={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClear();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            sx={{
              padding: '4px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <img 
              src={xIcon} 
              alt="Clear" 
              style={{ 
                width: '18px', 
                height: '18px',
                filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(200deg) brightness(104%) contrast(97%)'
              }} 
            />
          </IconButton>
        </components.DropdownIndicator>
      );
    }
    
    return <components.DropdownIndicator {...props} />;
  };

  // Custom styles for react-select to match the design
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#ffffff',
      border: state.isFocused || hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3',
      borderRadius: '8px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      paddingLeft: '12px',
      paddingRight: '4px',
      ...(isMobile && { touchAction: 'manipulation' }),
      '&:hover': {
        border: '1px solid #007BC6',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      height: '48px',
      minHeight: '48px',
      overflow: 'hidden',
      flex: 1,
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#000000',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: '#38485C',
      padding: '0',
      minWidth: '20px',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#F3F4F6',
      borderRadius: '6px',
      margin: '2px',
      maxWidth: '120px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#007BC6',
      fontSize: '14px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#6B7280',
      '&:hover': {
        backgroundColor: '#E5E7EB',
        color: '#374151',
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
      marginTop: '4px',
      zIndex: 10000,
      overflow: 'hidden',
      ...(isMobile && { touchAction: 'manipulation' }),
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10000,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
      ...(isMobile && { touchAction: 'manipulation' }),
    }),
    option: (provided, state) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 20px',
      ...(isMobile && { minHeight: '44px' }),
      backgroundColor: state.isSelected
        ? '#F0F8FF'
        : state.isFocused
          ? '#F3F4F6'
          : '#ffffff',
      color: state.isSelected ? '#027BC6' : '#333',
      fontSize: '16px',
      fontWeight: state.isSelected ? 700 : 600,
      fontFamily: 'Montserrat, sans-serif',
      cursor: 'pointer',
      borderBottom: '1px solid #E4E7EA',
      '&:last-child': {
        borderBottom: 'none',
      },
      '&:hover': {
        backgroundColor: state.isSelected ? '#E6F3FF' : '#F3F4F6',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      color: '#000000',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
    }),
  };

  // Debounced search handler
  const debouncedSearch = useCallback((searchTerm) => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      // Only trigger search if 2+ characters or empty (to clear)
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        onChange(searchTerm);
      }
    }, 300); // 300ms debounce delay
  }, [onChange]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setSearchValue(newValue);
    
    // For search inputs, use debounced search
    if (type === 'search') {
      debouncedSearch(newValue);
    } else {
      // For other inputs, call onChange immediately
      onChange(newValue);
    }
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}-${month}-${year}`;
  };

  const handleDateSelection = (date) => {
    if (type === 'daterange') {
      if (selectingStart) {
        setStartDate(date);
        setEndDate(null);
        setSelectingStart(false);
      } else if (date >= startDate) {
        setEndDate(date);
        setSelectingStart(true);
      } else {
        setStartDate(date);
        setEndDate(null);
      }
    } else if (type === 'date') {
      setStartDate(date);
      setShowDatePicker(false);
      onChange({
        display: formatDateForDisplay(date),
        date: date.toISOString(),
      });
    }
  };

  const resetSelection = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingStart(true);
    setShowDatePicker(false);
  };

  useEffect(() => {
    if (!showDatePicker) {
      isOpeningRef.current = false;
      return; // Only listen when picker is open
    }
    
    // Reset the opening flag after a short delay
    const resetOpeningFlag = setTimeout(() => {
      isOpeningRef.current = false;
    }, 100);
    
    const getTargetForEvent = (e) => {
      // Use touch coordinates for outside check (e.target can be wrong on touch)
      const touch = e.changedTouches && e.changedTouches[0];
      if (touch) {
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        return el || e.target;
      }
      return e.target;
    };

    const handleClickOutside = (e) => {
      if (isOpeningRef.current) return;
      const target = getTargetForEvent(e);
      const isInsidePicker = datePickerRef.current && datePickerRef.current.contains(target);
      const isInsideCalendar = datePickerCalendarRef.current && (
        datePickerCalendarRef.current.contains(target) ||
        (target.closest && target.closest('.MuiPickersDay-root') && datePickerCalendarRef.current.contains(target.closest('.MuiPickersDay-root')))
      );
      const isStickyHeader = target.closest && target.closest('[class*="sticky-header"]');

      if (!isInsidePicker && !isInsideCalendar) {
        const isOnStickyHeaderBackground = isStickyHeader && !isInsidePicker;
        if (isOnStickyHeaderBackground || !datePickerRef.current?.contains(target)) {
          setShowDatePicker(false);
        }
      }
    };

    // mousedown for mouse; touchend for touch so day selection registers before outside-close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchend', handleClickOutside, { passive: true });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resetOpeningFlag);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchend', handleClickOutside);
    };
  }, [showDatePicker]);

  // Notify parent when date picker opens/closes for page scroll space
  useEffect(() => {
    if ((type === 'dateRange' || type === 'daterange') && onDatePickerOpenChange) {
      onDatePickerOpenChange(showDatePicker);
    }
  }, [type, showDatePicker, onDatePickerOpenChange]);

  // Sync searchValue with external value prop
  useEffect(() => {
    setSearchValue(value || '');
  }, [value]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (value && typeof value === 'object') {
      if ('startDate' in value || 'endDate' in value) {
        setStartDate(value.startDate ? new Date(value.startDate) : null);
        setEndDate(value.endDate ? new Date(value.endDate) : null);
        setSelectingStart(!value.startDate || !!value.endDate);
        return;
      }
      if ('date' in value) {
        const parsedDate = new Date(value.date);
        if (!isNaN(parsedDate.getTime())) {
          setStartDate(parsedDate);
        }
        return;
      }
    } else {
      resetSelection();
    }

    if (typeof value === 'string') {
      const parsedDate = new Date(value);
      if (!isNaN(parsedDate.getTime())) {
        setStartDate(parsedDate);
      }
    } else {
      resetSelection();
    }
  }, [value]);

  const renderInputField = () => {
    switch (type) {
      case 'search':
        return (
          <Box sx={{ position: 'relative', flex: 1, minWidth: '150px', height: '48px' }}>
            <Box sx={{ 
              position: 'absolute', 
              left: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              zIndex: 1
            }}>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#AFB0B2" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M21 21l-4.486-4.494M19 10.5a8.5 8.5 0 1 1-17 0 8.5 8.5 0 0 1 17 0z"/>
              </svg>
            </Box>
            <TextField
              type="search"
              value={searchValue}
              onChange={handleChange}
              disabled={disabled}
              placeholder="Recherche..."
              fullWidth
              InputProps={{
                endAdornment: (searchValue && searchValue.length > 0) && onClear ? (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                      setSearchValue('');
                      onChange('');
                      onClear();
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    sx={{
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <img 
                      src={xIcon} 
                      alt="Clear" 
                      style={{ 
                        width: '18px', 
                        height: '18px',
                        filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(200deg) brightness(104%) contrast(97%)'
                      }} 
                    />
                  </IconButton>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  paddingLeft: '12px',
                  paddingRight: (searchValue && searchValue.length > 0) && onClear ? '4px' : '8px',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                  backgroundColor: '#ffffff',
                  border: (searchValue && searchValue.length > 0) ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  borderRadius: '8px',
                  '&:hover': {
                    border: '1px solid #007BC6',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #007BC6',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  '&::placeholder': {
                    color: '#A3A3A3',
                    opacity: 1,
                  },
                  '&::-webkit-search-cancel-button': {
                    display: 'none',
                  },
                  '&::-webkit-search-decoration': {
                    display: 'none',
                  },
                },
                '& .MuiInputLabel-root': {
                  display: 'none',
                },
              }}
            />
          </Box>
        );

      case 'dropdown':
        const getDisplayValue = (option) => {
          if (option.firstName && option.lastName) {
            return `${option.firstName} ${option.lastName}`;
          }
          return option.label || option.name || option.value;
        };

        // Use the isMultiSelect prop instead of hardcoded detection

        const getSelectedDisplay = () => {
          if (!value || (Array.isArray(value) && value.length === 0)) return `${label}: Tous`;
          
          if (isMultiSelect && Array.isArray(value)) {
            if (value.length === 0) return `${label}: Tous`;
            if (value.length === 1) {
              const selectedOption = options.find(opt => String(opt.id || opt.value) === String(value[0]));
              return selectedOption ? `${label}: ${getDisplayValue(selectedOption)}` : `${label}: Tous`;
            }
            return `${label}: ${value.length} sélectionnés`;
          } else if (!isMultiSelect) {
            const selectedOption = options.find(opt => String(opt.value) === String(value));
            if (selectedOption) {
              return `${label}: ${getDisplayValue(selectedOption)}`;
            }
          }
          return `${label}: Tous`;
        };

        return (
          <Box sx={{ flex: 1, minWidth: '150px' }} ref={selectWrapperRef}>
            <Select
            options={options.map((option) => ({
              value: option.value,
              label: getDisplayValue(option),
            }))}
            value={(() => {
              if (!value) return isMultiSelect ? [] : null;
              
              if (isMultiSelect && Array.isArray(value)) {
                const selectedOptions = options.filter(opt =>
                  Array.isArray(opt.value)
                    ? opt.value.every(id => value.includes(id))
                    : value.includes(opt.value)
                );
                if (selectedOptions.length > 0) {
                  return selectedOptions.map(opt => ({
                    value: opt.value,
                    label: getDisplayValue(opt)
                  }));
                }
                return value.map(val => {
                  const selectedOption = options.find(opt => String(opt.value) === String(val));
                  return selectedOption ? {
                    value: selectedOption.value,
                    label: getDisplayValue(selectedOption)
                  } : null;
                }).filter(Boolean);
              } else if (!isMultiSelect) {
                const selectedOption = options.find(opt => {
                const match = String(opt.value) === String(value);
                return match;
              });
                return selectedOption ? {
                  value: selectedOption.value,
                  label: getDisplayValue(selectedOption)
                } : null;
              }
              return isMultiSelect ? [] : null;
            })()}
            onChange={(selectedOptions) => {
              if (isMultiSelect) {
                const raw = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
                const values = raw.some(Array.isArray) ? raw.flat(Infinity) : raw;
                onChange && onChange(values);
              } else {
                onChange && onChange(selectedOptions ? selectedOptions.value : null);
              }
            }}
            placeholder={getSelectedDisplay()}
            styles={{
              ...customSelectStyles,
              option: (provided, state) => {
                const base = customSelectStyles.option(provided, state);
                const isHighlight = highlightOptionValues && state.data && highlightOptionValues.includes(state.data.value);
                return {
                  ...base,
                  color: isHighlight ? '#027BC6' : base.color,
                  fontWeight: isHighlight ? 700 : base.fontWeight,
                };
              },
              placeholder: (provided) => ({
                ...provided,
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif',
                color: '#000000',
              }),
            }}
            isDisabled={disabled}
            isSearchable={isSearchable !== undefined ? isSearchable : !isMultiSelect}
            isMulti={isMultiSelect}
            closeMenuOnSelect={!isMultiSelect}
            hideSelectedOptions={false}
            menuIsOpen={menuIsOpen}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            onMenuOpen={() => {
              setMenuIsOpen(true);
              lastActionRef.current = 'menu-open';
            }}
            onMenuClose={() => {
              if (isMultiSelect && shouldIgnoreCloseFromReactSelect()) {
                return;
              }
              setMenuIsOpen(false);
              lastActionRef.current = 'menu-close';
            }}
            onInputChange={(inputValue, { action }) => {
              if (isMultiSelect) {
                lastActionRef.current = action;
              }
            }}
            filterOption={createFilter({
              ignoreCase: true,
              trim: true,
              matchFrom: 'any',
            })}
            components={{
              Option: isMultiSelect
                ? (props) => <MultiselectOption {...props} />
                : (props) => {
                    const { innerProps, ...rest } = props;
                    const merged = innerProps ? { ...innerProps, onTouchEnd(e) { e.preventDefault(); innerProps.onClick?.(e); } } : undefined;
                    return <components.Option {...rest} {...(merged && { innerProps: merged })}>{props.children}</components.Option>;
                  },
              MultiValue: () => null, // Hide individual tags
              ValueContainer: isMultiSelect
                ? (props) => <MultiselectValueContainer {...props} label={label} getValue={props.getValue} />
                : ({ children, ...props }) => <components.ValueContainer {...props}>{children}</components.ValueContainer>,
              SingleValue: ({ children, ...props }) => (
                <components.SingleValue {...props}>
                  <span style={{ color: '#38485C' }}>{label}:&nbsp;</span>
                  <span style={{ color: '#007BC6', fontWeight: 600 }}>{children}</span>
                </components.SingleValue>
              ),
              Placeholder: ({ children, ...props }) => (
                <components.Placeholder {...props}>
                  <span style={{ color: '#38485C' }}>{label}:&nbsp;</span>
                  <span style={{ color: '#007BC6', fontWeight: 600 }}>Tous</span>
                </components.Placeholder>
              ),
              Input: (props) => (
                <components.Input {...props} style={{ ...props.style, minWidth: '20px' }} />
              ),
              ClearIndicator: null,
              DropdownIndicator: isMultiSelect
                ? (props) => <CustomDropdownIndicator {...props} hasValue={hasValue} onClear={() => { onClear(); setMenuIsOpen(false); }} isMulti={isMultiSelect} />
                : (props) => <SingleSelectDropdownIndicator {...props} hasValue={hasValue} onClear={() => { onClear(); setMenuIsOpen(false); }} />,
            }}
          />
          </Box>
        );

      case 'daterange':
        return (
          <Box sx={{ flex: 1, minWidth: '150px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <Box sx={{ position: 'relative', width: '100%' }} ref={datePickerRef}>
              <Box
                sx={{
                  width: '100%',
                  height: '48px',
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#ffffff',
                  border: hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': {
                    border: '1px solid #007BC6',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle: if open, close it; if closed, open it
                  if (showDatePicker) {
                    setShowDatePicker(false);
                  } else {
                    isOpeningRef.current = true;
                    setShowDatePicker(true);
                    setTimeout(() => {
                      isOpeningRef.current = false;
                    }, 100);
                  }
                }}
              >
                <Typography
                  sx={{
                    color: value?.start ? '#000000' : '#38485C',
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: 'Montserrat, sans-serif',
                  }}
                >
                  {value?.start && value?.end
                    ? `${formatDateForDisplay(value.start)} - ${formatDateForDisplay(value.end)}`
                    : value?.start
                      ? formatDateForDisplay(value.start)
                      : 'Date'}
                </Typography>
                <img 
                  src={calendarIcon} 
                  alt="Calendar" 
                  style={{ 
                    width: '24px', 
                    height: '24px',
                    filter: 'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1200%) hue-rotate(201deg) brightness(95%) contrast(86%)'
                  }} 
                />
              </Box>

              {showDatePicker && (
                <Box 
                  ref={(el) => {
                    if (el) {
                      // Store the calendar element reference for click outside detection
                      datePickerCalendarRef.current = el;
                    }
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    ...(isMobile
                      ? {
                          position: 'fixed',
                          zIndex: 1001,
                          top: '50%',
                          left: 16,
                          right: 16,
                          width: 'auto',
                          maxWidth: 'none',
                          minWidth: 0,
                          mt: 0,
                          transform: 'translateY(-50%)',
                          boxSizing: 'border-box',
                        }
                      : {
                          position: 'absolute',
                          zIndex: 1001,
                          top: '100%',
                          right: 0,
                          left: 'auto',
                          mt: 1,
                          minWidth: '320px',
                          width: 'max-content',
                        }),
                    maxHeight: 'calc(100vh - 80px)',
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                    border: hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  }}
                >
                  {/* Single Calendar */}
                  <DateCalendar
                    value={startDate}
                    onChange={(newDate) => {
                      if (!startDate) {
                        setStartDate(newDate);
                      } else if (!endDate) {
                        if (newDate >= startDate) {
                          setEndDate(newDate);
                        } else {
                          setStartDate(newDate);
                          setEndDate(null);
                        }
                      } else {
                        setStartDate(newDate);
                        setEndDate(null);
                      }
                    }}
                    slots={{ day: CustomPickersDay }}
                    slotProps={{
                      calendarHeader: {
                        sx: {
                          '& .MuiPickersCalendarHeader-label': {
                            textTransform: 'none',
                            fontFamily: 'Montserrat',
                            fontWeight: 700,
                            fontSize: '20px',
                          },
                          '& .MuiPickersArrowSwitcher-root': {
                            '& .MuiIconButton-root': {
                              color: '#000000',
                              fontWeight: 'bold',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiDayCalendar-weekDayLabel': {
                        fontFamily: 'Avenir',
                        fontWeight: 900,
                        fontSize: '10px',
                        lineHeight: '16px',
                        letterSpacing: '0.2px',
                        color: '#3E3E3E',
                        opacity: 0.5,
                        width: '40px !important',
                        minWidth: '40px !important',
                        flex: '0 0 40px !important',
                        textAlign: 'center !important',
                        margin: '0 2px !important',
                      },
                      '& .MuiDayCalendar-weekContainer': {
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        gap: '4px',
                      },
                      '& .MuiPickersDay-root': {
                        width: '40px !important',
                        height: '40px !important',
                        margin: '0 !important',
                        display: 'flex !important',
                        alignItems: 'center !important',
                        justifyContent: 'center !important',
                        minWidth: '40px !important',
                        flex: '0 0 40px !important',
                        touchAction: 'manipulation',
                        '@media (max-width: 768px)': {
                          width: '44px !important',
                          height: '44px !important',
                          minWidth: '44px !important',
                          flex: '0 0 44px !important',
                        },
                      },
                    }}
                  />

                  <Box sx={{ p: 1 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      gap: { xs: 0.5, sm: 1 }, 
                      justifyContent: 'flex-end',
                      flexWrap: { xs: 'wrap', sm: 'nowrap' }
                    }}>
                      <Button
                        type="button"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setStartDate(null);
                          setEndDate(null);
                          setShowDatePicker(false);
                          onChange && onChange(null);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          setStartDate(null);
                          setEndDate(null);
                          setShowDatePicker(false);
                          onChange && onChange(null);
                        }}
                        sx={{
                          padding: { xs: '6px 12px', sm: '8px 16px' },
                          backgroundColor: 'transparent',
                          color: '#919191',
                          border: 'none',
                          borderRadius: '12px',
                          fontFamily: 'Montserrat',
                          fontWeight: 600,
                          fontSize: { xs: '12px', sm: '14px' },
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textTransform: 'none',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          touchAction: 'manipulation',
                        }}
                      >
                        Réinitialiser
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDatePicker(false);
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          setShowDatePicker(false);
                        }}
                        sx={{
                          padding: { xs: '6px 12px', sm: '8px 16px' },
                          backgroundColor: 'transparent',
                          color: '#027BC6',
                          border: 'none',
                          borderRadius: '12px',
                          fontFamily: 'Montserrat',
                          fontWeight: 600,
                          fontSize: { xs: '12px', sm: '14px' },
                          cursor: 'pointer',
                          textTransform: 'none',
                          height: '34px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          touchAction: 'manipulation',
                        }}
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.nativeEvent.stopImmediatePropagation();
                          setShowDatePicker(false);
                          if (startDate && endDate) {
                            onChange && onChange({ start: startDate, end: endDate });
                          } else if (startDate) {
                            onChange && onChange({ start: startDate, end: startDate });
                          }
                          return false;
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowDatePicker(false);
                          if (startDate && endDate) {
                            onChange && onChange({ start: startDate, end: endDate });
                          } else if (startDate) {
                            onChange && onChange({ start: startDate, end: startDate });
                          }
                        }}
                        sx={{
                          padding: { xs: '6px 12px', sm: '8px 16px' },
                          backgroundColor: '#027BC6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0px 4px 10px #6A78F819',
                          fontFamily: 'Montserrat',
                          fontWeight: 600,
                          fontSize: { xs: '12px', sm: '14px' },
                          cursor: 'pointer',
                          width: '104px',
                          height: '34px',
                          textTransform: 'none',
                          touchAction: 'manipulation',
                          '&:hover': {
                            backgroundColor: '#025a9c',
                          },
                        }}
                      >
                        Appliquer
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </LocalizationProvider>
          </Box>
        );

      case 'date':
        return (
          <Box sx={{ flex: 1, minWidth: '150px' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <Box sx={{ position: 'relative', width: '100%' }} ref={datePickerRef}>
              <Box
                sx={{
                  width: '100%',
                  height: '48px',
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: '#ffffff',
                  border: hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  '&:hover': {
                    border: '1px solid #007BC6',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Toggle: if open, close it; if closed, open it
                  if (showDatePicker) {
                    setShowDatePicker(false);
                  } else {
                    isOpeningRef.current = true;
                    setShowDatePicker(true);
                    setTimeout(() => {
                      isOpeningRef.current = false;
                    }, 100);
                  }
                }}
              >
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: 'Montserrat, sans-serif',
                    color: startDate ? '#000000' : '#000000',
                  }}
                >
                  {startDate ? formatDateForDisplay(startDate) : 'Date'}
                </Typography>
                <img 
                  src={calendarIcon} 
                  alt="Calendar" 
                  style={{ 
                    width: '24px', 
                    height: '24px',
                    filter: 'brightness(0) saturate(100%) invert(45%) sepia(8%) saturate(1200%) hue-rotate(201deg) brightness(95%) contrast(86%)'
                  }} 
                />
              </Box>

              {showDatePicker && (
                <Box 
                  ref={(el) => {
                    datePickerCalendarRef.current = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    position: 'absolute',
                    zIndex: 1001,
                    mt: 1,
                    backgroundColor: '#ffffff',
                    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                    p: 2,
                }}>
                  <LocalizationProvider 
                    dateAdapter={AdapterDateFns} 
                    adapterLocale={fr}
                  >
                    <DateCalendar
                    value={startDate}
                    onChange={(date) => {
                      handleDateSelection(date);
                    }}
                  />
                  </LocalizationProvider>
                  <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', gap: 1.5 }}>
                    <Box
                      component="button"
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: '12px',
                        color: '#027BC6',
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#EDEFFF',
                        },
                      }}
                      onClick={resetSelection}
                    >
                      Annuler
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </LocalizationProvider>
          </Box>
        );

      case 'amountrange':
        return (
          <Box sx={{ position: 'relative', flex: 1, minWidth: '150px', height: '48px' }}>
            <TextField
              value={value || ''}
              onChange={handleChange}
              disabled={disabled}
              placeholder={placeholder || 'Plage de montants'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '48px',
                  paddingLeft: '19px',
                  paddingRight: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  backgroundColor: '#ffffff',
                  border: hasValue ? '2px solid #007BC6' : '1px solid #E3E3E3',
                  borderRadius: '8px',
                  '&:hover': {
                    border: '1px solid #027BC6',
                  },
                  '&.Mui-focused': {
                    border: '1px solid #027BC6',
                  },
                  '& fieldset': {
                    border: 'none',
                  },
                },
                '& .MuiInputBase-input': {
                  '&::placeholder': {
                    color: '#A3A3A3',
                    opacity: 1,
                  },
                },
                '& .MuiInputLabel-root': {
                  display: 'none', // Hide any floating labels
                },
              }}
            />
          </Box>
        );

      default:
        return (
          <Box sx={{ flex: 1, minWidth: '150px' }}>
            <TextField
            {...props}
            type={type}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                paddingLeft: '19px',
                paddingRight: '8px',
                fontSize: '16px',
                fontWeight: 500,
                backgroundColor: '#ffffff',
                border: '1px solid #E3E3E3',
                borderRadius: '8px',
                '&:hover': {
                  border: '1px solid #6A79F8',
                },
                '&.Mui-focused': {
                  border: '1px solid #6A79F8',
                },
                '& fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                '&::placeholder': {
                  color: '#A3A3A3',
                  opacity: 1,
                },
              },
              '& .MuiInputLabel-root': {
                display: 'none', // Hide any floating labels
              },
            }}
          />
          </Box>
        );
    }
  };

  return (
    <Box
      sx={{
        flexShrink: 0,
        flex: '1 1 calc(15% - 1rem)',
        minWidth: '150px',
      }}
    >
      {renderInputField()}
    </Box>
  );
};

export default FilterInput;